import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Calculator as A1, GraduationCap as A2, Wallet as A3, Clock as A4, Award as A5, Scale as A6 } from 'lucide-react';

import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, GraduationCap, DollarSign, Calculator, TrendingUp, Building,
  MapPin, Users, BarChart3, AlertTriangle, ChevronRight, PiggyBank, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import FederalAidMeter from '../components/FederalAidMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function TuitionCalculatorPage() {
  const [arrived, setArrived] = useState(false);
  const { user, token } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inState, setInState] = useState(false);
  const [years, setYears] = useState(4);
  const [savedColleges, setSavedColleges] = useState([]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/colleges/favorites`, { headers: { Authorization: `Bearer ${token}` } });
        setSavedColleges((res.data.favorites || []).filter((c) => c && c.name));
      } catch { /* no saved colleges yet */ }
    })();
  }, [token]);

  const searchColleges = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await axios.post(`${API}/colleges/search`, { query, limit: 10 });
      setResults(res.data.results || []);
      if (res.data.results?.length === 0) toast.info('No colleges found. Try a different search.');
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Load a saved college straight into the calculator. If it lacks a scorecard
  // id, fall back to searching by name so the user still lands on it fast.
  const loadSavedCollege = async (college) => {
    if (college.id) {
      selectCollege(college);
      return;
    }
    setQuery(college.name);
    setSearching(true);
    try {
      const res = await axios.post(`${API}/colleges/search`, { query: college.name, limit: 5 });
      const match = (res.data.results || [])[0];
      if (match) selectCollege(match);
      else { setResults(res.data.results || []); toast.info('Pick the exact campus from the results.'); }
    } catch {
      toast.error('Could not load that college');
    } finally {
      setSearching(false);
    }
  };

  const selectCollege = async (college) => {
    setSelected(college);
    setResults([]);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/colleges/${college.id}/cost-calculator?years=${years}&in_state=${inState}`);
      setCostData(res.data);
    } catch {
      toast.error('Failed to load cost data');
    } finally {
      setLoading(false);
    }
  };

  const recalculate = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/colleges/${selected.id}/cost-calculator?years=${years}&in_state=${inState}`);
      setCostData(res.data);
    } catch {
      toast.error('Recalculation failed');
    } finally {
      setLoading(false);
    }
  };

  const annualBreakdown = costData ? [
    { name: 'Tuition', value: costData.annual_breakdown.tuition, color: '#3b82f6' },
    { name: 'Room & Board', value: costData.annual_breakdown.room_and_board, color: '#8b5cf6' },
    { name: 'Books', value: costData.annual_breakdown.books_supplies, color: '#10b981' },
    { name: 'Personal', value: costData.annual_breakdown.personal_expenses || 0, color: '#f59e0b' },
    { name: 'Transport', value: costData.annual_breakdown.transportation || 0, color: '#ec4899' },
  ].filter(i => i.value > 0) : [];

  const yearlyProjection = costData ? Array.from({ length: years }, (_, i) => ({
    year: `Year ${i + 1}`,
    cost: costData.annual_breakdown.total_annual,
    net: costData.financial_aid.avg_net_price_annual,
  })) : [];

  const content = (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <Calculator className="w-7 h-7 text-blue-400" /> Tuition Cost Calculator
        </h1>
        <p className="text-slate-300 mt-1">Search any college and get a real-time cost breakdown with loan projections</p>
      </div>

      {/* Search */}
      <GlassCard data-testid="college-search-card">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchColleges()}
              placeholder="Search colleges (e.g. Duke University, MIT, Georgia Tech)"
              className="pl-10"
              data-testid="college-search-input"
            />
          </div>
          <Button onClick={searchColleges} disabled={searching} data-testid="college-search-btn">
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </div>

        {/* Results dropdown */}
        <AnimatePresence>
          {results.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-3 space-y-1 max-h-[300px] overflow-y-auto" data-testid="search-results">
              {results.map((r) => (
                <button key={r.id} onClick={() => selectCollege(r)}
                  className="w-full text-left p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-blue-500/30 transition-colors"
                  data-testid={`result-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{r.name}</p>
                      <p className="text-slate-300 text-sm flex items-center gap-2">
                        <MapPin className="w-3 h-3" /> {r.city}, {r.state}
                        {r.ownership && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700">{r.ownership}</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      {r.tuition_out_of_state && <p className="text-white text-sm font-medium">{fmt(r.tuition_out_of_state)}/yr</p>}
                      {r.admission_rate && <p className="text-slate-300 text-xs">{(r.admission_rate * 100).toFixed(0)}% acceptance</p>}
                    </div>
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </GlassCard>

      {/* Your saved colleges, one-tap prefill */}
      {savedColleges.length > 0 && !selected && (
        <GlassCard data-testid="saved-colleges-card">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-amber-400" />
            <h3 className="text-white font-semibold text-sm">Your saved colleges</h3>
            <span className="text-slate-300 text-xs">tap to calculate instantly</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {savedColleges.map((c, i) => (
              <button
                key={c.id || c.name || i}
                onClick={() => loadSavedCollege(c)}
                className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-700/60 hover:border-amber-400/40 hover:bg-slate-800 transition-colors"
                data-testid={`saved-college-${i}`}
              >
                <GraduationCap className="w-4 h-4 text-amber-300" />
                <span className="text-slate-200 text-sm">{c.name}</span>
                {(c.city || c.state) && <span className="text-slate-300 text-xs hidden sm:inline">{[c.city, c.state].filter(Boolean).join(', ')}</span>}
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-amber-300 transition-colors" />
              </button>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Selected College + Options */}
      {selected && (
        <GlassCard data-testid="selected-college">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{selected.name}</h2>
                <p className="text-slate-300 text-sm">{selected.city}, {selected.state} &bull; {selected.ownership || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={inState} onChange={(e) => setInState(e.target.checked)} className="rounded" />
                In-state resident
              </label>
              <select value={years} onChange={(e) => setYears(Number(e.target.value))}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm" data-testid="years-select">
                <option value={2}>2 years</option>
                <option value={4}>4 years</option>
                <option value={5}>5 years</option>
                <option value={6}>6 years</option>
              </select>
              <Button size="sm" onClick={recalculate} data-testid="recalculate-btn">Recalculate</Button>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Cost Breakdown */}
      {loading && <LoadingSpinner />}
      {costData && !loading && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {/* Summary Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Sticker Price', value: fmt(costData.total_cost), icon: DollarSign, color: 'red', sub: `${years} years` },
              { label: 'Est. Financial Aid', value: fmt(costData.financial_aid.estimated_annual_aid * years), icon: GraduationCap, color: 'emerald', sub: `${fmt(costData.financial_aid.estimated_annual_aid)}/yr` },
              { label: 'Est. Net Cost', value: fmt(costData.financial_aid.total_net_cost), icon: BarChart3, color: 'blue', sub: 'After aid' },
              { label: 'Monthly Loan Payment', value: `$${Math.round(costData.loan_projection.monthly_payment)}`, icon: TrendingUp, color: 'amber', sub: `10yr @ ${costData.loan_projection.interest_rate}%` },
            ].map((card, i) => (
              <GlassCard key={i} data-testid={`cost-card-${i}`}>
                <card.icon className={`w-6 h-6 text-${card.color}-400 mb-2`} />
                <p className="text-2xl font-bold text-white">{card.value}</p>
                <p className="text-slate-300 text-sm">{card.label}</p>
                <p className={`text-xs text-${card.color}-400 mt-1`}>{card.sub}</p>
              </GlassCard>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Annual Breakdown Pie */}
            <GlassCard>
              <h3 className="text-white font-semibold mb-4">Annual Cost Breakdown</h3>
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={annualBreakdown} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" stroke="none">
                      {annualBreakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 flex-1">
                  {annualBreakdown.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-slate-300">{item.name}</span>
                      </div>
                      <span className="text-white font-medium">{fmt(item.value)}</span>
                    </div>
                  ))}
                  <div className="pt-2 border-t border-slate-700 flex items-center justify-between text-sm font-semibold">
                    <span className="text-slate-300">Total/Year</span>
                    <span className="text-white">{fmt(costData.annual_breakdown.total_annual)}</span>
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Year-by-Year Comparison */}
            <GlassCard>
              <h3 className="text-white font-semibold mb-4">Sticker Price vs. Net Cost</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={yearlyProjection}>
                  <XAxis dataKey="year" stroke="#C6D4E8" fontSize={12} />
                  <YAxis stroke="#C6D4E8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="cost" name="Sticker Price" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="net" name="Net Cost" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-3 text-xs text-slate-300">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> Sticker Price</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500" /> After Aid</span>
              </div>
            </GlassCard>
          </div>

          {/* Loan Projection */}
          <GlassCard data-testid="loan-projection">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-amber-400" /> Loan Repayment Projection
            </h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <p className="text-3xl font-bold text-white">{fmt(costData.loan_projection.loan_amount)}</p>
                <p className="text-slate-300 text-sm">Total Borrowed</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <p className="text-3xl font-bold text-amber-400">{fmt(costData.loan_projection.total_repaid)}</p>
                <p className="text-slate-300 text-sm">Total Repaid (10 yrs)</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <p className="text-3xl font-bold text-red-400">{fmt(costData.loan_projection.total_interest)}</p>
                <p className="text-slate-300 text-sm">Interest Paid</p>
              </div>
            </div>
            {costData.median_earnings_after && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-emerald-400 text-sm font-medium">Median Earnings 10 Years After Enrollment</p>
                  <p className="text-white font-semibold">{fmt(costData.median_earnings_after)}/year</p>
                </div>
              </div>
            )}
          </GlassCard>

          {/* Federal Aid Eligibility Meter, OBBBA-aware loan caps vs. gap */}
          <FederalAidMeter
            totalCost={costData.total_cost}
            annualCost={costData.annual_breakdown.total_annual}
            years={years}
            borrowerType="undergrad"
            dependencyStatus={((user?.age ?? 0) >= 24 || user?.role === 'professional') ? 'independent' : 'dependent'}
            yearInProgram={1}
            estimatedAnnualAid={costData.financial_aid.estimated_annual_aid}
          />

          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-amber-500/25 bg-amber-500/[0.07]"
            data-testid="tuition-disclaimer">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
            <p className="text-xs text-amber-50/85">
              College Scorecard figures, the aid office has the current price. Estimates, not quotes.
            </p>
          </div>
        </motion.div>
      )}

      {/* Empty state */}
      {!selected && !searching && (
        <GlassCard className="text-center py-12">
          <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Search for a College</h3>
          <p className="text-slate-300 max-w-md mx-auto">
            Type a college name above to get a detailed cost breakdown including tuition, room & board, estimated financial aid, and loan projections.
          </p>
        </GlassCard>
      )}
    </div>
  );

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="tuition-arrival"
        eyebrow="Education Costs"
        title="Price the training before you commit."
        subtitle="Degree, certificate, bootcamp or apprenticeship, what each one costs at a specific school, what aid usually covers, and what you would be left to find."
        accent="#fbbf24"
        second="#34d399"
        ctaLabel="Price my education"
        narration={"This is Education Costs. Instead of a national average, you price the actual thing you are considering, the school, the programme, the length, and see tuition, fees, living costs and the aid that typically applies. What is left is your real gap, and that is the number worth planning around rather than the brochure figure."}
        facts={[
          { icon: A1, head: "Real programmes", sub: "Priced at a specific school, not an average" },
          { icon: A5, head: "Aid included", sub: "What grants and scholarships usually cover" },
          { icon: A3, head: "Your gap", sub: "What is genuinely left for you to find" }
        ]}
        modules={[
          { icon: A2, label: "Choose the route", line: "Degree, certificate, bootcamp or apprenticeship", hue: "#fbbf24" },
          { icon: A1, label: "Full cost", line: "Tuition, fees, living and materials by year", hue: "#22d3ee" },
          { icon: A5, label: "Aid applied", line: "Grants and awards typical for your situation", hue: "#34d399" },
          { icon: A3, label: "The gap", line: "What remains, and the cheapest ways to close it", hue: "#a78bfa" },
          { icon: A4, label: "Time cost", line: "Years in, and the earnings you give up", hue: "#f472b6" },
          { icon: A6, label: "Compare options", line: "Two or three routes side by side", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return user ? <AppLayout>{content}</AppLayout> : (
    <div className="min-h-screen bg-navy-gradient">
      <div className="pt-24 pb-16 px-4 md:px-12">{content}</div>
    </div>
  );
}
